import React, { useState } from 'react';
import styled from 'astroturf/react';
import pluralize from 'pluralize';

import { Icon } from 'js/design_system/Icon';
import { Stat } from 'js/design_system/Stat';
import { TextButton } from 'js/design_system/TextButton';
import { Truncate } from 'js/design_system/Truncate';
import { Author } from 'js/liswp/Sidebar/hooks/types';
import formatNumber from 'js/utils/format';

import AuthorCard from './AuthorCard';
import { SidebarSection } from './SidebarSection';

// How many authors appear at first, before the "View All" button is clicked
const AUTHORS_LIMIT = 3;

const StatGroup = styled.div`
  display: flex;
  gap: 40px;
`;

const ViewAllPapersLink = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0; /* Allows flex child to shrink below content size */
  width: 100%;
  align-self: stretch;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:active,
  &:focus {
    text-decoration: none;
  }
`;

const LinkText = styled.span`
  color: var(--text-link, #0645b1);
  font-family: var(--type-font-family-sans-serif, 'DM Sans');
  font-size: var(--type-body-md-size, 16px);
  font-style: normal;
  font-weight: 600;
  line-height: var(--type-body-md-line-height, 20px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1 1 0;
  min-width: 0; /* Allows truncation to work */
`;

const LinkIcon = styled(Icon)`
  color: var(--text-link, #0645b1);
  flex-shrink: 0; /* Prevents icon from shrinking */
`;

const AuthorStats: React.FC<{
  author: Author;
}> = ({ author }) => {
  if (!author.papers && !author.followers) {
    return null;
  }

  return (
    <StatGroup>
      {author.papers ? (
        <Stat label="Papers" value={formatNumber(author.papers)} />
      ) : null}
      {author.followers ? (
        <Stat label="Followers" value={formatNumber(author.followers)} />
      ) : null}
    </StatGroup>
  );
};

type AboutTheAuthorsProps = {
  authors: Author[];
  onFollow: (author: Author) => void;
  onMessage: (author: Author) => void;
  showViewAllPapersLink?: boolean;
};

const AboutTheAuthors: React.FC<AboutTheAuthorsProps> = ({
  authors,
  onFollow,
  onMessage,
  showViewAllPapersLink = false,
}) => {
  const [limited, setLimited] = useState(true);

  const singleAuthor = authors.length === 1;
  const multipleAuthors = authors.length > 1;
  const hasMoreThanThreeAuthors = authors.length > AUTHORS_LIMIT;

  return (
    <SidebarSection
      heading={`About the ${pluralize('author', authors.length)}`}
      compact={singleAuthor}
    >
      {(limited ? authors.slice(0, AUTHORS_LIMIT) : authors).map((author) => (
        <AuthorCard
          key={author.id}
          author={author}
          onFollow={onFollow}
          onMessage={onMessage}
        />
      ))}

      {singleAuthor && (
        <>
          <Truncate lines={4}>{authors[0].bio}</Truncate>
          <AuthorStats author={authors[0]} />
          {/* Only shows on the LOSWP for now. See AboutTheAuthorsSSR */}
          {showViewAllPapersLink && authors[0].url && (
            <ViewAllPapersLink href={authors[0].url}>
              <LinkText>View all papers from {authors[0].displayName}</LinkText>
              <LinkIcon name="arrow_forward" size={18} />
            </ViewAllPapersLink>
          )}
        </>
      )}

      {multipleAuthors && limited && hasMoreThanThreeAuthors && (
        <TextButton
          inline
          iconRight="keyboard_arrow_down"
          onClick={() => setLimited(false)}
        >
          View all
        </TextButton>
      )}
    </SidebarSection>
  );
};

export default AboutTheAuthors;
